import { MULTITENANT_CONSTANTS, TENANT } from "../Resources/Multitenant/tenantConstants";
import { URL_BASE_V2, URL_SGCA } from "./servicesConstants";

// Capa de acceso a la API de la página "Buscar" (OTT-1966, Fase 1).
// Contrato: docs/buscar-descubre-analisis.md y "CONTRATO-FRONT-buscar-descubre" del diseño.
// El `id` de interfaz (GET /v2/sergio/{tenant}/interfaz) sigue obteniéndose con getMenu()
// de ./services.js; aquí solo se consume, ya guardado en Redux `commonState.interfazId`.

// Orden explícito del contrato (sección 2.4). Se ordena por `date` (comentario de Diego Dozo en
// OTT-1966, 2026-09-22): el BACK unificó start (VOD que viene de un live) y created_on (VOD
// subido directo a la OTT) en un único campo `date`, que es el mismo valor que usa tanto para
// ordenar como para filtrar por fecha (from/to) — no hay que mirar ninguna otra fecha. Sustituye
// a los intentos anteriores con `start`/`publish_date`/`created_on` (quedaban obsoletos junto con
// esos campos, que el BACK ha retirado de esta respuesta).
//
// OJO despliegue: comprobado que `sort=date` y el campo `date` en la respuesta ya funcionan en
// PRE (`streamer-cdn-pre.ott.tiivii.com`), pero en PRO (`streamer-cdn.ott.tiivii.com`) esta API
// todavía no los acepta (400 «Invalid sort field, must be one of: created_on, publish_date,
// start, title, id», sin `date` en la respuesta) — no desplegar este cambio de front a PRO antes
// de que el BACK haya promocionado el suyo.
export const BUSCAR_SORT = {
  RECENT: "-date",
  OLDEST: "date",
  ALPHABETICAL: "title",
};

const tenantServer = () => MULTITENANT_CONSTANTS[TENANT].tenantServer;

const handleJsonResponse = async (response) => {
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    const error = new Error(`GET ${response.url} failed ${response.status}`);
    error.status = response.status;
    error.body = text?.slice(0, 200);
    throw error;
  }
  return response.json();
};

/**
 * @typedef {Object} FilterNodeTranslation
 * @property {string} language
 * @property {string} name
 *
 * @typedef {Object} FilterNode
 * @property {number} id
 * @property {string} name
 * @property {number|null} parent
 * @property {number} order
 * @property {Object} filters
 * @property {boolean} has_children
 * @property {FilterNodeTranslation[]} translations
 *
 * @typedef {Object} FilterNodesResponse
 * @property {FilterNode[]} data
 */

/**
 * GET /v2/sgca/{tenant}/nodes-root?interfaz={id} — lista del modal "Filtros".
 * `data` vacío => no mostrar el botón "Filtros" (contrato 2.2).
 * @param {number|string} [interfazId]
 * @param {{signal?: AbortSignal}} [opts]
 * @returns {Promise<FilterNodesResponse>}
 */
export const getNodesRoot = (interfazId, { signal } = {}) => {
  const params = new URLSearchParams();
  if (interfazId != null) params.set("interfaz", interfazId);
  return fetch(`${URL_BASE_V2}${URL_SGCA}${tenantServer()}/nodes-root?${params.toString()}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    signal,
  }).then(handleJsonResponse);
};

/**
 * GET /v2/sgca/{tenant}/nodes-children/{id}?interfaz={id} — hijos de un nodo padre.
 * Llamar al desplegar un padre con `has_children: true`, o para precargarlos.
 * @param {number|string} nodeId
 * @param {number|string} [interfazId]
 * @param {{signal?: AbortSignal}} [opts]
 * @returns {Promise<FilterNodesResponse>}
 */
export const getNodesChildren = (nodeId, interfazId, { signal } = {}) => {
  const params = new URLSearchParams();
  if (interfazId != null) params.set("interfaz", interfazId);
  return fetch(`${URL_BASE_V2}${URL_SGCA}${tenantServer()}/nodes-children/${nodeId}?${params.toString()}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    signal,
  }).then(handleJsonResponse);
};

/**
 * @typedef {Object} ScreenBackgroundImage
 * @property {string} full_url
 * @property {string} [url]
 * @property {string} [asset_url]
 * @property {Array<Object>} [thumbnails]
 * @property {Object|null} [embed]
 *
 * @typedef {Object} ScreenItemTranslation
 * @property {string} title
 * @property {string|null} subtitle
 * @property {string|null} description
 * @property {string} language
 *
 * @typedef {Object} ScreenItem "Páginas" (contrato §2.5): misma forma que los hijos de un
 * widget list-screen/list-screen-grid/list-screen-v2 de la home, más los datos de la página de
 * destino. `title`/`translations[].title` son el título de la TARJETA (lo que se pinta,
 * getWidgetTitle); `screen_title` es el título de la PÁGINA de destino (por el que filtra el
 * back al buscar por texto, no se pinta en la tarjeta).
 * @property {number} id
 * @property {string} title
 * @property {"screen"} type
 * @property {number|{id:number}} screen id de la página de destino (o el objeto con ese id)
 * @property {string} screen_title
 * @property {string|null} screen_subtitle
 * @property {string|null} screen_description
 * @property {string|null} backgroundcolor
 * @property {{data: ScreenBackgroundImage}|null} backgroundimage
 * @property {number} order
 * @property {string|null} timezone
 * @property {Object|null} schedules
 * @property {Array<Object>} parents
 * @property {ScreenItemTranslation[]} translations
 *
 * @typedef {Object} ContentCategory
 * @property {number} id
 * @property {string} code
 * @property {Array<Object>} translations
 *
 * @typedef {Object} ContentItem
 * @property {number} id
 * @property {string} type
 * @property {string} status
 * @property {boolean} serial
 * @property {string} title
 * @property {string|null} subtitle
 * @property {string|null} description
 * @property {string} date fecha unificada (start si viene de un live, created_on si no), ISO con
 *   el desfase horario ya aplicado; no convertir a UTC para sacar el día (usar date.slice(0, 10)).
 *   Se usa para mostrar, filtrar y ordenar (comentario de Diego Dozo en OTT-1966, 2026-09-22;
 *   solo disponible en PRE por ahora).
 * @property {number|null} start inicio en segundos (epoch); con `finish`, decide la etiqueta de
 *   estado de los eventos en directo (EventStatusTag en ResultCard.js).
 * @property {number|null} finish fin en segundos (epoch); `null` en contenidos sin fin (VOD).
 * @property {string|null} poster
 * @property {string|null} banner
 * @property {string|null} background
 * @property {ContentCategory|null} category
 * @property {ContentCategory|null} subcategory
 * @property {string[]} tags
 * @property {string|null} author
 * @property {{id:number,code:string}|null} rating
 * @property {number} valoration
 * @property {number} votes
 * @property {Object} configuration
 * @property {Array<Object>} translations
 *
 * @typedef {Object} SearchResponseFlat
 * @property {{filter_count:number, kind?:"contents"|"screens", screensSection?:boolean, screensHiddenByFilters?:boolean}} meta
 * `meta.kind` distingue una vista de sección de páginas (contrato §2.5); si no viene, es
 * "contents" (compat con respuestas de orch anteriores a esta funcionalidad).
 * @property {Array<ContentItem|ScreenItem>} data
 * @property {number} limit
 * @property {number} page
 * @property {string} sort
 *
 * @typedef {Object} SearchSection
 * @property {number} id
 * @property {string} name
 * @property {Array<Object>} translations
 * @property {number} count
 * @property {"contents"|"screens"} [kind] igual que `meta.kind`, pero por sección; sin `kind`
 * se trata como "contents" (contrato §1: "Si `kind` no viene, trátalo como contents").
 * @property {Array<ContentItem|ScreenItem>} data
 *
 * @typedef {Object} SearchResponseGrouped
 * @property {{total:number, grouped:true, screensSection?:boolean, screensHiddenByFilters?:boolean}} meta
 * @property {SearchSection[]} sections
 * @property {number} limit
 * @property {string} sort
 *
 * @typedef {Object} BuscarSearchParams
 * @property {string} [text] -> filter[value][contains]
 * @property {number[]} [nodes] ids de nodos marcados en el modal, combinados con OR
 * @property {string} [from] YYYY-MM-DD, inclusivo
 * @property {string} [to] YYYY-MM-DD, inclusivo
 * @property {"-date"|"date"|"title"} [sort] por defecto BUSCAR_SORT.RECENT
 * @property {number|string} [section] activa la vista de una sección (respuesta plana)
 * @property {number} [limit]
 * @property {number} [page]
 * @property {number|string} [interfazId]
 */

/**
 * GET /v2/sgca/{tenant}/search — única llamada de resultados de la página (carga inicial,
 * texto, filtros, fecha, orden, vista de sección y lazy loading). Envía siempre `grouped=true`
 * y `sort`; nunca `searchfilter=` ni `filter[status]` (retirados, ver Fase 4).
 * @param {BuscarSearchParams} params
 * @param {{signal?: AbortSignal}} [opts]
 * @returns {Promise<SearchResponseFlat|SearchResponseGrouped>} el front distingue la forma
 * por la presencia de `sections`, nunca por configuración propia.
 */
export const searchBuscar = (params = {}, { signal } = {}) => {
  const { text, nodes, from, to, sort, section, limit, page, interfazId } = params;
  const query = new URLSearchParams();
  query.set("grouped", "true");
  query.set("sort", sort || BUSCAR_SORT.RECENT);
  query.set("searchin", "title,tags");
  if (text) query.set("filter[value][contains]", text);
  if (nodes && nodes.length) query.set("nodes", nodes.join(","));
  if (from) query.set("from", from);
  if (to) query.set("to", to);
  if (section != null) query.set("section", section);
  if (limit != null) query.set("limit", limit);
  if (page != null) query.set("page", page);
  if (interfazId != null) query.set("interfaz", interfazId);

  return fetch(`${URL_BASE_V2}${URL_SGCA}${tenantServer()}/search?${query.toString()}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    signal,
  }).then(handleJsonResponse);
};

/** Respuesta agrupada (B) vs. plana (A): el contrato dice que el layout lo decide la presencia
 * de `sections`, nunca configuración propia del front. */
export const isGroupedSearchResponse = (response) => Array.isArray(response?.sections);

/** "Páginas" (contrato §2.5, §1: "Si `kind` no viene, trátalo como contents"). */
export const isScreensKind = (kind) => kind === "screens";

/**
 * Resuelve la imagen de una tarjeta de página a una URL plana: `backgroundimage` en el
 * contrato de /buscar llega anidado (`{data:{full_url,...}}`), pero ScreenCard (extraída de
 * los widgets list-screen* de la home) espera una string, igual que ya le llega desde ahí.
 * @param {ScreenItem} item
 * @returns {string|null}
 */
export const resolveScreenBackgroundImage = (item) => {
  const bg = item?.backgroundimage;
  if (!bg) return null;
  if (typeof bg === "string") return bg;
  return bg?.data?.full_url || bg?.data?.url || bg?.data?.asset_url || null;
};
